import {
    Alert,
    Box,
    Button,
    Dialog,
    IconButton,
    MenuItem,
    Stack,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    TextField,
    Typography,
    CircularProgress,
    useMediaQuery,
    DialogTitle,
    DialogContent, Tooltip
} from "@mui/material";
import SmartTextArea from "src/components/smart-text-ares";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { FileUploadSection } from "src/components/file-upload-with-view";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useUserSpecialties from "src/hooks/use-userSpecialties";
import { useFormik } from "formik";
import * as Yup from "yup";
import { INFO } from "src/libs/log";
import { extendedProfileApi } from "src/pages/cabinet/profiles/my/data/extendedProfileApi";
import { ReviewRequestMessageArea } from "src/components/review-request-message-edit-area";
import { SpecialtySelectForm } from 'src/components/specialty-select-form'
import { tr } from 'src/locales/tr';

const validationSchema = Yup.object().shape({
    projectName: Yup.string().required(tr("Project title is required")),
    location: Yup.string().required(tr("Project location is required")),
    specialty: Yup.string().required(tr("Specialty is required")),
    email: Yup.string()
        .email(tr("Invalid email address"))
        .required(tr("Email is required")),
    message: Yup.string().required(tr("Message is required"))
});

export const ProjectWithReviewRequestDialog = ({
    profile,
    open,
    onClose,
    onSubmit,
    currentRequest,
    isEditMode,
    existingRequests
}) => {
    const { userSpecialties, isFetching: isFetchingUserSpecialties } = useUserSpecialties(profile.id);
    const [localSpecialties, setLocalSpecialties] = useState([]);
    const [addSpecOpen, setAddSpecOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

    useEffect(() => setLocalSpecialties(userSpecialties), [userSpecialties]);

    const formik = useFormik({
        initialValues: {
            projectName: currentRequest.projectName || '',
            date: currentRequest.date || null,
            specialty: currentRequest.specialty || '',
            projectDescription: currentRequest.projectDescription || '',
            files: currentRequest.files || [],
            email: currentRequest.email || '',
            message: currentRequest.message || '',
            location: currentRequest.location || ''
        },
        validationSchema,
        onSubmit: (values) => {
            INFO('Submitting review request', values);
            onSubmit(values);
            onClose();
            formik.resetForm();
            setActiveStep(0);
        },
        enableReinitialize: true
    });

    const handlePublishOnly = () => {
        const values = {
            ...formik.values,
            email: null,
            message: null
        };
        onSubmit(values);
        onClose();
        formik.resetForm();
        setActiveStep(0);
    };

    const handleDrop = (newFiles) => {
        const formattedFiles = newFiles.map((file) => ({
            file,
            preview: URL.createObjectURL(file),
            type: file.type.startsWith('video') ? 'video' : 'image',
        }));
        formik.setFieldValue('files', [...formik.values.files, ...formattedFiles]);
    };

    const handleRemove = (index) => {
        const newFiles = [...formik.values.files];
        newFiles.splice(index, 1);

        formik.setFieldValue('files', newFiles);
    };

    const handleRemoveAll = () => {
        formik.setFieldValue('files', []);
    };

    const handleSpecialtyAdded = async (newSpec) => {
        try {
            await extendedProfileApi.addSpecialties(profile.id, newSpec.id);

            setLocalSpecialties((prev) =>
                prev.some((s) => s.id === newSpec.id) ? prev : [...prev, newSpec]
            );

            formik.setFieldValue('specialty', newSpec.id);

            INFO('Specialty added from portfolio modal', newSpec);
        } catch (err) {
            console.error('Failed to add specialty', err);
        } finally {
            setAddSpecOpen(false);
        }
    };

    const handleUpdateFiles = useCallback((newFile) => {
        if (newFile) {
            formik.setFieldValue('files',
                formik.values.files.map((file, index) => {
                    if (file.preview === newFile.preview) {
                        return newFile;
                    }
                    return file;
                })
            );
        }
    }, [formik.values.files]);

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!re.test(email)) {
            return tr("Please enter a valid email address");
        }
        if (existingRequests?.some((request) => request.email === email)) {
            return tr("Only one review request per email address is allowed");
        }
        return undefined;
    };

    const renderSpecialtySelect = () => {
        if (!isFetchingUserSpecialties) {
            return (
                <TextField
                    fullWidth
                    label={tr("Specialty from your list of services")}
                    disabled
                    InputProps={{
                        endAdornment: <CircularProgress size={20} />
                    }}
                    helperText={tr("Loading specialties...")}
                    error={formik.touched.specialty && Boolean(formik.errors.specialty)}
                />
            );
        }

        // if (!userSpecialties?.length) {
        //     return (
        //         <TextField
        //             fullWidth
        //             label="Specialty from your list of services"
        //             disabled
        //             helperText="You haven't added any specialties yet"
        //             error={formik.touched.specialty && Boolean(formik.errors.specialty)}
        //         />
        //     );
        // }

        return (
            <TextField
                select
                fullWidth
                label={tr("Specialty from your list of services")}
                name="specialty"
                required
                value={formik.values.specialty}
                onChange={(e) => {
                    if (e.target.value === '__add_new__') {
                        setAddSpecOpen(true);
                    } else {
                        formik.handleChange(e);
                    }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.specialty && Boolean(formik.errors.specialty)}
                helperText={tr(formik.touched.specialty) && tr(formik.errors.specialty)}
            >
                {localSpecialties.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                        {tr(s.label)}
                    </MenuItem>
                ))}

                <MenuItem value="__add_new__" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                    {tr("＋ Add a new specialty…")}
                </MenuItem>
            </TextField>
        );
    };

    const isContinueDisabled = () => {
        if (activeStep === 0) {
            return !formik.values.projectName || !formik.values.specialty;
        }
        return false;
    };

    const isPublishDisabled = () => {
        if (activeStep === 0) {
            return !formik.values.projectName || !formik.values.specialty || !formik.values.date || !formik.values.projectDescription;
        }
        return false;
    };

    const steps = [
        {
            label: tr("Project Details"),
            description: tr("Add information about the project"),
            content: (
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            label={tr("Title")}
                            name="projectName"
                            required
                            placeholder={tr("ex. Door Installation")}
                            value={formik.values.projectName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.projectName && Boolean(formik.errors.projectName)}
                            helperText={formik.touched.projectName && formik.errors.projectName}
                        />
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={tr("Date *")}
                                required

                                value={formik.values.date}
                                onChange={(date) => formik.setFieldValue('date', date)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        error={formik.touched.date && Boolean(formik.errors.date)}
                                        helperText={formik.touched.date && formik.errors.date}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Box>
                    <TextField
                        fullWidth
                        label={tr("Location")}
                        name="location"
                        required
                        placeholder={tr("enter the project location")}
                        value={formik.values.location}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.location && Boolean(formik.errors.location)}
                        helperText={formik.touched.location && formik.errors.location}
                    />
                    {renderSpecialtySelect()}
                    <TextField
                        fullWidth
                        label={tr("Project Description")}
                        name="projectDescription"
                        placeholder={tr("ex. Customer needs a door installed in their home.")}
                        multiline
                        minRows={2}
                        maxRows={10}
                        required
                        value={formik.values.projectDescription}
                        onChange={formik.handleChange}
                    />
                    <FileUploadSection
                        files={formik.values.files}
                        onDrop={handleDrop}
                        onRemove={handleRemove}
                        onRemoveAll={handleRemoveAll}
                        accept={{ 'image/*,video/*': [] }}
                        caption={tr("Attach photos or videos")}
                        onUpdate={handleUpdateFiles}
                        updateFields={[{
                            placeholder: tr("Describe what's shown in this image..."),
                            label: tr("Description"),
                            name: 'description',
                            multiline: true,
                            minRows: 3,
                            maxRows: 4
                        }]}
                    />
                    <Alert
                        severity="info"
                        variant="standard"
                        sx={{ fontSize: '12px' }}
                        icon={<ArrowDownwardIcon fontSize="small" />}
                    >
                        {tr("You can also send a request to your client for this project to leave a review on the CTMASS platform.")}
                    </Alert>
                </Stack>
            )
        },
        {
            label: tr("Client Information"),
            description: tr("Add client details and message"),
            content: (
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <Alert severity="info" variant={"standard"} sx={{ fontSize: '12px' }}>
                        {tr("The link to your profile and the review form will be added automatically to the footer of the letter.")}
                    </Alert>
                    <TextField
                        fullWidth
                        label={tr("Client Email")}
                        name="email"
                        type="email"
                        value={formik.values.email}
                        onChange={(e) => {
                            formik.handleChange(e);
                            const error = validateEmail(e.target.value);
                            formik.setFieldError('email', error);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        required
                    />

                    <ReviewRequestMessageArea
                        profile={profile}
                        label={tr("Message")}
                        initialValue={formik.values.message}
                        name="message"
                        onTextChange={(value) => formik.setFieldValue('message', value)}
                        error={formik.touched.message && Boolean(formik.errors.message)}
                        helperText={formik.touched.message && formik.errors.message}
                    />
                </Stack>
            )
        }
    ];

    const handleClose = () => {
        formik.resetForm();
        setActiveStep(0);
        onClose();
    }

    const handleNext = () => {
        let isValid = true;
        if (activeStep === 0) {
            formik.setTouched({
                projectName: true,
                date: true,
                specialty: true
            });
            isValid = !formik.errors.projectName && !formik.errors.date && !formik.errors.specialty;
        }

        if (isValid) {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        formik.setFieldValue("email", null);
        formik.setFieldValue("message", null);
        setActiveStep((prev) => prev - 1);
    }

    const handleStepClick = (index) => {
        if (index < activeStep) {
            setActiveStep(index);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="md"
                fullScreen={!mdUp}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">{tr("Publish Project to portfolio")}</Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Alert severity="info">
                        {tr("You need to fill in the information about the completed project by marking the required fields with an asterisk (*). You can also provide information about the client (step 2) to request their feedback on this project.")}
                    </Alert>
                </DialogTitle>
                <DialogContent>
                    <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 2 }}>
                        {steps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel
                                    optional={index < steps.length - 1 ? (
                                        <Typography variant="caption">{step.description}</Typography>
                                    ) : null}
                                    onClick={() => handleStepClick(index)}
                                    sx={{ cursor: index < activeStep ? 'pointer' : 'default' }}
                                >
                                    {tr(step.label)}
                                </StepLabel>
                                <StepContent>
                                    {step.content}
                                    <Box sx={{ mb: 2, mt: 2 }}>
                                        <Stack direction="row" spacing={2}>
                                            <Tooltip
                                                title={tr("Publish the project in your portfolio and send a request for feedback to your former client.")}
                                            >
                                                <Button
                                                    onClick={index === steps.length - 1 ? handleBack : handleNext}
                                                    disabled={isContinueDisabled()}
                                                >
                                                    {index === steps.length - 1 ? tr("Cancel review request") : tr("Create Review Request")}
                                                </Button>
                                            </Tooltip>
                                            <Box sx={{ flexGrow: 1 }} />
                                            <Button
                                                onClick={handleClose}
                                                color="error"
                                            >
                                                {tr("Cancel")}
                                            </Button>
                                            <Tooltip
                                                title={tr("Publish the project in your portfolio and send a request for feedback to your former client.")}>
                                                <Button
                                                    variant="contained"
                                                    color="info"
                                                    onClick={formik.handleSubmit}
                                                    disabled={!formik.isValid || formik.isSubmitting}
                                                    sx={{ ...(index === 0 && { display: 'none' }) }}
                                                >
                                                    {tr("Publish & Send Review Request")}
                                                </Button>
                                            </Tooltip>
                                            <Tooltip
                                                title={tr("Only publish the project in your portfolio without sending a review request.")}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handlePublishOnly}
                                                    disabled={isPublishDisabled() || formik.isSubmitting}
                                                    sx={{ ...(index === 1 && { display: 'none' }) }}
                                                >
                                                    {tr("Publish project")}
                                                </Button>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                </DialogContent>
            </Dialog>

            <SpecialtySelectForm
                open={addSpecOpen}
                onClose={() => setAddSpecOpen(false)}
                onSpecialtyChange={handleSpecialtyAdded}
                selectedSpecialties={localSpecialties}
            />
        </>
    );
};

ProjectWithReviewRequestDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    currentRequest: PropTypes.object.isRequired,
    isEditMode: PropTypes.bool,
    existingRequests: PropTypes.array
};